import { useLiveQuery } from 'dexie-react-hooks';
import { useContext } from 'react';
import { db } from '@/lib/db/index';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { SyncContext } from '@/contexts/SyncContext';
import { useAuth } from '@/contexts/AuthContext';

export interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  failedCount: number;
  lastSyncedAt: string | null;
}

/**
 * Returns live sync state. `pendingCount` and `failedCount` update reactively
 * via Dexie's useLiveQuery whenever the sync_queue table changes.
 */
export function useSyncStatus(): SyncStatus {
  const { user } = useAuth();
  const orgId = user?.organization_id ?? null;
  const isOnline = useOnlineStatus();
  const ctx = useContext(SyncContext);

  const pendingCount = useLiveQuery(
    () => orgId
      ? db.sync_queue.where('[org_id+status]').equals([orgId, 'pending']).count()
      : Promise.resolve(0),
    [orgId],
    0,
  ) ?? 0;

  const failedCount = useLiveQuery(
    () => orgId
      ? db.sync_queue.where('[org_id+status]').equals([orgId, 'failed']).count()
      : Promise.resolve(0),
    [orgId],
    0,
  ) ?? 0;

  return {
    isOnline,
    isSyncing: ctx?.isSyncing ?? false,
    pendingCount,
    failedCount,
    lastSyncedAt: ctx?.lastSyncedAt ?? null,
  };
}
